import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../api';
import {
  BookOpen, CheckCircle, Clock, Play, FileText, Link2, Video,
  Star, AlertTriangle, GraduationCap, ChevronRight, Award, Download
} from 'lucide-react';

const MyTraining = ({ user, setUser }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [quizModule, setQuizModule] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await api.get('/training/my-assignments');
      setAssignments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const getModuleStatus = (assignment, moduleId) => {
    const mp = (assignment.module_progress || []).find(m => m.module_id === moduleId);
    return mp?.status || 'pending';
  };

  const getModuleScore = (assignment, moduleId) => {
    const mp = (assignment.module_progress || []).find(m => m.module_id === moduleId);
    return mp?.quiz_score;
  };

  const openQuiz = (assignment, module) => {
    setQuizModule({ assignment, module });
    setAnswers(new Array(module.quiz.questions.length).fill(-1));
    setQuizResult(null);
  };

  const submitQuiz = async () => {
    if (!quizModule) return;
    if (answers.some(a => a === -1)) { toast.error('Please answer all questions'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/training/assignments/${quizModule.assignment.id}/modules/${quizModule.module.id}/quiz`,
        { answers }
      );
      setQuizResult(res.data);
      fetchAssignments();
      if (res.data.passed) {
        toast.success(`Passed! Score: ${res.data.score}%`);
      } else {
        toast.error(`Score: ${res.data.score}% (need ${res.data.passing_score}%)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quiz');
    } finally { setSubmitting(false); }
  };

  const markModuleComplete = async (assignmentId, moduleId) => {
    try {
      await api.put(`/training/assignments/${assignmentId}/modules/${moduleId}/complete`);
      toast.success('Module completed');
      fetchAssignments();
      if (selectedAssignment) {
        const res = await api.get(`/training/assignments/${assignmentId}`);
        setSelectedAssignment(res.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadCertificate = async (assignmentId, assignment) => {
    setDownloading(assignmentId);
    try {
      const res = await api.get(`/training/certificates/${assignmentId}/download`, { responseType: 'blob' });
      // Extract filename from Content-Disposition header, or build from assignment data
      const contentDisp = res.headers?.['content-disposition'] || '';
      const serverFilename = contentDisp.match(/filename="?([^";\n]+)"?/)?.[1];
      const fallbackName = `Certificate_${(assignment?.employee_code || assignmentId).replace(/\s/g, '_')}_${(assignment?.course_name || 'Training').replace(/\s/g, '_')}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFilename || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (err) { toast.error('Failed to download certificate'); }
    finally { setDownloading(null); }
  };

  const getStatusBadge = (s) => ({
    assigned: <Badge className="bg-slate-100 text-slate-700">Not Started</Badge>,
    in_progress: <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>,
    completed: <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>,
    pending: <Badge className="bg-slate-100 text-slate-600">Pending</Badge>,
    failed: <Badge className="bg-red-100 text-red-700">Retry</Badge>,
  }[s] || <Badge variant="outline">{s}</Badge>);

  const completed = assignments.filter(a => a.status === 'completed').length;
  const pending = assignments.filter(a => a.status !== 'completed').length;

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="my-training-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">My Training</h1>
          <p className="text-slate-500 text-sm">Complete your assigned training courses and assessments</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-indigo-400">
            <CardContent className="p-4 flex items-center gap-3">
              <BookOpen size={24} className="text-indigo-500" />
              <div>
                <p className="text-xs text-slate-500">Total Courses</p>
                <p className="text-2xl font-bold text-slate-800">{assignments.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-400">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock size={24} className="text-amber-500" />
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-amber-700">{pending}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-400">
            <CardContent className="p-4 flex items-center gap-3">
              <Award size={24} className="text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-700">{completed}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Cards */}
        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading...</div>
        ) : assignments.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <GraduationCap size={40} className="mx-auto mb-3 text-slate-300" />
            <p>No training courses assigned yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {assignments.map(a => (
              <Card key={a.id} className={`hover:shadow-md transition-shadow cursor-pointer ${a.status === 'completed' ? 'border-emerald-200 bg-emerald-50/20' : ''}`}
                onClick={() => setSelectedAssignment(a)} data-testid={`course-card-${a.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-800">{a.course_name}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {(a.course?.modules || []).length} modules
                        {a.mandatory && <span className="text-red-400 ml-2">Mandatory</span>}
                      </p>
                    </div>
                    {getStatusBadge(a.status)}
                  </div>
                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Progress</span>
                      <span>{a.progress_percentage}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${a.progress_percentage >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${a.progress_percentage}%` }} />
                    </div>
                  </div>
                  {/* Module dots */}
                  <div className="flex items-center gap-1.5">
                    {(a.module_progress || []).map((mp, i) => (
                      <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        mp.status === 'completed' ? 'bg-emerald-500 text-white' : mp.status === 'failed' ? 'bg-red-400 text-white' : 'bg-slate-200 text-slate-500'
                      }`} title={mp.module_title}>{i + 1}</div>
                    ))}
                  </div>
                  {a.due_date && (
                    <p className="text-xs text-slate-400 mt-2">Due: {new Date(a.due_date).toLocaleDateString('en-IN')}</p>
                  )}
                  {a.status === 'completed' && (
                    <Button size="sm" variant="outline" className="mt-3 w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      onClick={(e) => { e.stopPropagation(); downloadCertificate(a.id, a); }}
                      disabled={downloading === a.id} data-testid={`download-cert-${a.id}`}>
                      <Download size={14} className="mr-1.5" />
                      {downloading === a.id ? 'Generating...' : 'Download Certificate'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ==================== COURSE DETAIL ==================== */}
      {selectedAssignment && (
        <Dialog open={!!selectedAssignment} onOpenChange={() => setSelectedAssignment(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen size={18} className="text-indigo-500" />
                {selectedAssignment.course_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedAssignment.status)}
                <span className="text-sm text-slate-500">{selectedAssignment.progress_percentage}% complete</span>
                {selectedAssignment.mandatory && <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>}
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${selectedAssignment.progress_percentage >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${selectedAssignment.progress_percentage}%` }} />
              </div>

              {selectedAssignment.status === 'completed' && (
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={() => downloadCertificate(selectedAssignment.id, selectedAssignment)}
                  disabled={downloading === selectedAssignment.id}
                  data-testid="download-cert-detail">
                  <Award size={16} className="mr-2" />
                  {downloading === selectedAssignment.id ? 'Generating Certificate...' : 'Download Certificate'}
                </Button>
              )}

              {selectedAssignment.course?.description && (
                <p className="text-sm text-slate-600">{selectedAssignment.course.description}</p>
              )}

              {/* Modules */}
              {(selectedAssignment.course?.modules || []).map((m, i) => {
                const status = getModuleStatus(selectedAssignment, m.id);
                const score = getModuleScore(selectedAssignment, m.id);
                const hasQuiz = m.quiz && m.quiz.questions?.length > 0;

                return (
                  <Card key={i} className={status === 'completed' ? 'border-emerald-200 bg-emerald-50/30' : ''} data-testid={`module-card-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {status === 'completed' ? (
                            <CheckCircle size={18} className="text-emerald-500" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex items-center justify-center text-xs text-slate-400">{i + 1}</div>
                          )}
                          <h4 className="font-medium text-slate-800">{m.title}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          {score !== null && score !== undefined && (
                            <Badge variant="outline" className={score >= (m.quiz?.passing_score || 70) ? 'text-emerald-600' : 'text-red-600'}>
                              Score: {score}%
                            </Badge>
                          )}
                          {getStatusBadge(status)}
                        </div>
                      </div>

                      {m.description && <p className="text-sm text-slate-500 mb-3">{m.description}</p>}

                      {/* Materials */}
                      {(m.materials || []).length > 0 && (
                        <div className="mb-3 space-y-1">
                          {m.materials.map((mat, j) => (
                            <a key={j} href={mat.url} target="_blank" rel="noreferrer"
                              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-1.5 rounded transition-colors">
                              {mat.type === 'video' ? <Video size={14} /> : mat.type === 'link' ? <Link2 size={14} /> : <FileText size={14} />}
                              {mat.title || 'View Material'}
                              <ChevronRight size={12} className="ml-auto" />
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      {status !== 'completed' && (
                        <div className="flex gap-2 mt-2">
                          {hasQuiz ? (
                            <Button size="sm" onClick={() => openQuiz(selectedAssignment, m)} data-testid={`take-quiz-${i}`}>
                              <Play size={14} className="mr-1" /> {status === 'failed' ? 'Retry Quiz' : 'Take Quiz'}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => markModuleComplete(selectedAssignment.id, m.id)} data-testid={`complete-module-${i}`}>
                              <CheckCircle size={14} className="mr-1" /> Mark Complete
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== QUIZ DIALOG ==================== */}
      {quizModule && (
        <Dialog open={!!quizModule} onOpenChange={() => { setQuizModule(null); setQuizResult(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star size={18} className="text-amber-500" />
                Quiz: {quizModule.module.title}
              </DialogTitle>
            </DialogHeader>

            {quizResult ? (
              /* Quiz Results */
              <div className="text-center py-6" data-testid="quiz-result">
                <div className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center text-3xl font-bold mb-4 ${
                  quizResult.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`}>
                  {quizResult.score}%
                </div>
                <h3 className={`text-xl font-bold ${quizResult.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                  {quizResult.passed ? 'Congratulations! You Passed!' : 'Not Passed'}
                </h3>
                <p className="text-slate-500 mt-2">
                  {quizResult.correct}/{quizResult.total} correct | Passing score: {quizResult.passing_score}%
                </p>
                <div className="flex gap-2 justify-center mt-6">
                  {!quizResult.passed && (
                    <Button onClick={() => { setQuizResult(null); setAnswers(new Array(quizModule.module.quiz.questions.length).fill(-1)); }}>
                      Retry Quiz
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { setQuizModule(null); setQuizResult(null); }}>Close</Button>
                </div>
              </div>
            ) : (
              /* Quiz Questions */
              <div className="space-y-5" data-testid="quiz-form">
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                  <p className="text-amber-700">
                    {quizModule.module.quiz.questions.length} questions | Passing score: {quizModule.module.quiz.passing_score}%
                  </p>
                </div>

                {quizModule.module.quiz.questions.map((q, qIdx) => (
                  <div key={qIdx} className="bg-slate-50 p-4 rounded-lg" data-testid={`quiz-question-${qIdx}`}>
                    <p className="font-medium text-slate-800 mb-3">Q{qIdx + 1}. {q.text}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <label key={oIdx} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          answers[qIdx] === oIdx ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}>
                          <input type="radio" name={`q-${qIdx}`} checked={answers[qIdx] === oIdx}
                            onChange={() => { const a = [...answers]; a[qIdx] = oIdx; setAnswers(a); }}
                            className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-center pt-4 border-t">
                  <p className="text-sm text-slate-500">{answers.filter(a => a !== -1).length}/{quizModule.module.quiz.questions.length} answered</p>
                  <Button onClick={submitQuiz} disabled={submitting || answers.some(a => a === -1)} data-testid="submit-quiz-btn">
                    {submitting ? 'Submitting...' : 'Submit Quiz'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default MyTraining;
